import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { UserSearchFilters } from "./useUserSearch";

interface UsersToolbarProps {
    query: string;
    filters: UserSearchFilters;
    canImportUsers: boolean;
    canCreateUsers: boolean;
    onQueryChange: (query: string) => void;
    onFiltersChange: (filters: UserSearchFilters) => void;
    onImport: () => void;
    onCreate: () => void;
}

const filterOptions = [
    { key: "includeStaff", label: "Staff", icon: "user-tie", active: "bg-blue-100 text-blue-800 border-blue-200" },
    { key: "includeStudents", label: "Students", icon: "graduation-cap", active: "bg-green-100 text-green-800 border-green-200" },
    { key: "includeParents", label: "Parents", icon: "users", active: "bg-purple-100 text-purple-800 border-purple-200" },
    { key: "includeGuests", label: "Guests", icon: "user-plus", active: "bg-orange-100 text-orange-800 border-orange-200" },
] as const;

export const UsersToolbar = ({
    query,
    filters,
    canImportUsers,
    canCreateUsers,
    onQueryChange,
    onFiltersChange,
    onImport,
    onCreate,
}: UsersToolbarProps) => (
    <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex-1 max-w-2xl">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={["fad", "search"]} className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="search"
                            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            value={query}
                            onChange={(event) => onQueryChange(event.target.value)}
                            placeholder="Search users by name, email, phone, or ID..."
                            aria-label="Search users"
                        />
                    </div>

                    <div className="mt-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Filter by user type:</div>
                        <div className="flex flex-wrap gap-2">
                            {filterOptions.map((option) => {
                                const enabled = filters[option.key];
                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        aria-pressed={enabled}
                                        onClick={() => onFiltersChange({ ...filters, [option.key]: !enabled })}
                                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                            enabled ? option.active : "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200"
                                        }`}
                                    >
                                        <FontAwesomeIcon icon={["fad", option.icon] as any} className="w-3 h-3 mr-1.5" />
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    {canImportUsers && (
                        <button type="button" onClick={onImport} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                            <FontAwesomeIcon icon={["fad", "file-import"]} className="w-4 h-4 mr-2" />
                            Import Users
                        </button>
                    )}
                    {canCreateUsers && (
                        <button type="button" onClick={onCreate} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                            <FontAwesomeIcon icon={["fad", "user-plus"]} className="w-4 h-4 mr-2" />
                            Create User
                        </button>
                    )}
                </div>
            </div>
        </div>
    </div>
);
